import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as featureActions from '@core-lib/store/user-store/actions';
import { catchError, delay, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Injectable } from '@angular/core';
import { AuthSharedService } from '@core-lib/services/auth.service';
import { UserService } from '@core-lib/services/user.service';
import { environment } from '@environments/environment';
import { Noop } from '../shared-store/actions';
import { ToastrService } from 'ngx-toastr';
import { CarpaService } from '@core-lib/services/carpa.service';
import { userHasPermission } from '@core-lib/functions/user.function';
import { UserPermission } from '@core-lib/enums/user-permission.enum';

@Injectable()
export class UserStoreEffects {
	constructor(
		private actions$: Actions,
		private authService: AuthSharedService,
		private carpaService: CarpaService,
		private userService: UserService,
		private toastr: ToastrService
	) {}

	userRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.LoadUserRequest),
			switchMap(action =>
				this.userService.getUser().pipe(
					switchMap(user => {
						const isSuperAdmin = userHasPermission(user.permissions, UserPermission.IsSuperAdmin);
						let tenantKey: string = this.authService.currentTenantKey;

						if (isSuperAdmin) {
							tenantKey = 'super-admin';
						} else {
							const tenantKeyValidForUser = user.tenants.some(t => t.tenantKey == tenantKey);
							if (!tenantKeyValidForUser) {
								tenantKey = user.tenants[0]?.tenantKey;
							}

							if (!tenantKey) {
								this.toastr.error(
									"Aucune Carpa valide associé à votre compte utilisateur n'a pu être chargée. Vous allez être déconnecté.",
									'Erreur'
								);
								return of(featureActions.LoadUserFailure({ error: null }));
							}
						}

						this.authService.setTenantKey(tenantKey);
						return [featureActions.LoadUserSuccess({ entity: user })];
					}),
					catchError(error => {
						return of(featureActions.LoadUserFailure({ error }));
					})
				)
			)
		)
	);

	userTenantRequestError$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.LoadUserFailure),
			delay(5000), // Allow the user to read the error message before logging out
			switchMap(action => {
				if (environment.isFront) {
					this.authService.logout(); // TODO: déplacer ailleurs
				}
				return [Noop()];
			})
		)
	);

	usersTenantRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.LoadUsersTenantRequest),
			switchMap(action =>
				this.userService.getUsersForTenant().pipe(
					map(userList => featureActions.LoadUsersTenantSuccess({ entity: userList.items })),
					catchError(error => of(featureActions.LoadUsersTenantFailure({ error })))
				)
			)
		)
	);

	carpasRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.LoadCarpasRequest),
			switchMap(action =>
				this.carpaService.getUserCarpas().pipe(
					switchMap(carpas => {
						return [featureActions.LoadCarpasSuccess({ entity: carpas })];
					}),
					catchError(error => of(featureActions.LoadCarpasFailure({ error })))
				)
			)
		)
	);

	loadCarpaRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.LoadCurrentCarpaRequest),
			switchMap(_ =>
				this.carpaService.getCurrent().pipe(
					map(carpa => featureActions.LoadCurrentCarpaSuccess({ entity: carpa })),
					catchError(error => of(featureActions.LoadCurrentCarpaFailure({ error })))
				)
			)
		)
	);
}
